import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeInformedConsentForVideo } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface InformedConsentForVideoFormData {
    student: string;
    formId: string;
    isRecordChild: boolean;
    isRecordAmongUs: boolean;
    isClinicalUse: boolean;
    isEducationalUse: boolean;
    isTrackingUse: boolean;
    completedBy: string | null;
    fullName: string;
    relTo: string;
    todayDate: string | null;
    signature: string;
}

interface InformedConsentForVideoFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<InformedConsentForVideoFormData, InformedConsentForVideoFormProps>;

const validators: Validators<InformedConsentForVideoFormData> = {
    isRecordChild: [required({ message: "It's required." })],
    isRecordAmongUs: [required({ message: "It's required." })],
    isClinicalUse: [required({ message: "It's required." })],
    isEducationalUse: [required({ message: "It's required." })],
    isTrackingUse: [required({ message: "It's required." })],
    completedBy: [required({ message: "It's required." }), maxLength({ max: 255 })],
    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    relTo: [required({ message: "It's required." }), maxLength({ max: 255 })],
    todayDate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
    signature: [required({ message: 'Signature is required.' }), maxLength({ max: 255 })],
};

const InformedConsentForVideoForm: React.FunctionComponent<InjectedProps & InformedConsentForVideoFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & InformedConsentForVideoFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Consent for Video Recording</h2>
            <div>
                Video materials are a valuable part of medical records. They provide clinicians with an in-depth look at
                parent-child interactions, help clinicians deliver effective treatment, allow parents to review their
                own interactions with the clinician, and demonstrate for families the changes that occur over time with
                treatment. Videos also provide a valuable training tool.
            </div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={17}
                wrapperConfig={{
                    label: '',
                }}
            />

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    I give permission to Manhattan Psychology Group (MPG) to record my child and myself in session with the clinician.
                </div>
                <FieldInput
                    name="isRecordChild"
                    type="checkbox"
                    validate={validators.isRecordChild}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    I give permission to MPG to record the conversation between and among us.
                </div>
                <FieldInput
                    name="isRecordAmongUs"
                    type="checkbox"
                    validate={validators.isRecordAmongUs}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    I understand that I have three options for how video material can be used and that I must check each option I approve. If I do not check an option, it is NOT approved.:
                </div>
            </div>



            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    Clinical Use: This option is to approve clinical use of video material. My clinician will use the video(s) to help me, as the client, better understand my own behavior and/or interactions with my child and capture changes in treatment. This option is for viewing only by the MPG clinician(s) related to my and/or my child's treatment.
                </div>
                <FieldInput
                    name="isClinicalUse"
                    type="checkbox"
                    validate={validators.isClinicalUse}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    Educational Use Internal to MPG: This option is to approve educational use of the video material within MPG to demonstrate the effectiveness of treatment, show how behavior changes over time with treatment, train clinicians on how to deliver treatment, and demonstrate how to manage different kinds of behavior. This option is for viewing by MPG clinicians and trainees for educational purposes.
                </div>
                <FieldInput
                    name="isEducationalUse"
                    type="checkbox"
                    validate={validators.isEducationalUse}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    Tracking Use External to MPG: This option is to approve educational use of the video material by MPG clinicians for workshops and conferences conducted at MPG and outside of MPG. MPG clinicians are teachers and train other clinicians in this treatment, and to help other learn why this treatment is valuable for individuals/families to be able to access. This option is for viewing by MPG clinicians, trainees, and professionals outside of MPG for educational purposes.
                </div>
                <FieldInput
                    name="isTrackingUse"
                    type="checkbox"
                    validate={validators.isTrackingUse}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.textBox)}>
                In all of these options, your child's and your full name (first and last) will not be identified. MPG will take all necessary precautions to assure confidentiality of protected health information in accordance with applicable legal requirements and the terms of your HIPAA consent form.
            </div>

            <div className={classNames(styles.textBox)}>
                Video recording consent form provided by:
            </div>
            <FieldInput
                name="completedBy"
                type="text"
                validate={validators.completedBy}
                wrapperConfig={{
                    label: 'This form was completed by: *',
                }}
            />
            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Name of client: *',
                }}
            />
            <FieldInput
                name="relTo"
                type="text"
                validate={validators.relTo}
                wrapperConfig={{
                    label: 'My relationship to the client: *',
                }}
            />

            <FieldDate
                name="todayDate"
                type="text"
                date={true}
                validate={validators.todayDate}
                wrapperConfig={{
                    label: "Today's date *",
                }}
            />

            <FieldInput
                name="signature"
                type="text"
                validate={validators.signature}
                wrapperConfig={{
                    label: 'Signature of person completing the form: *',
                }}
            />


            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<InformedConsentForVideoFormData, InformedConsentForVideoFormProps>({
    form: 'informed-consent-for-testing-form',
    enableReinitialize: true,
    onSubmit: async (
        data: InformedConsentForVideoFormData,
        dispatch: Dispatch<any>,
        props: InformedConsentForVideoFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeInformedConsentForVideo({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<InformedConsentForVideoFormData, InformedConsentForVideoFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(InformedConsentForVideoForm);
